import apiClient from '../client';
import type { AddCriterionRequestV2, CriterionAPIV2 } from '../../types';

// NOTE: `baseURL` override is the same temporary workaround as the other v2
// modules until apiClient is refactored to a `/api` base with versioned paths.
const V2_BASE = '/api/v2';

export type CriterionStatus = 'ACTIVE' | 'INACTIVE';

/**
 * Activate or deactivate a criterion. Returns the updated criterion (incl. its
 * latest change attribution). `userId` is the current user's id; the backend
 * records who/when/why. `reason` may be null.
 */
export async function patchCriterionV2(
  tenderId: string,
  criterionId: string,
  status: CriterionStatus,
  userId: string | null,
  reason: string | null,
): Promise<CriterionAPIV2> {
  const { data } = await apiClient.patch<CriterionAPIV2>(
    `/tenders/${tenderId}/criteria/${criterionId}`,
    { action: { status, userId, reason } },
    { baseURL: V2_BASE },
  );
  return data;
}

/**
 * Recruiter role-assignment of a criterion — the write behind the "Needs a role"
 * triage tab. Binds the criterion to the given position ordinals, replacing any
 * prior binding: a subset scopes it, every ordinal marks it "Not role-specific"
 * (universal), an empty list returns it to held out. 204 on success.
 */
export async function assignCriterionPositionsV2(
  tenderId: string,
  criterionId: string,
  positionOrdinals: number[],
): Promise<void> {
  await apiClient.put<void>(
    `/tenders/${tenderId}/criteria/${criterionId}/positions`,
    { positionOrdinals },
    { baseURL: V2_BASE },
  );
}

/**
 * Add a recruiter-supplied criterion to a tender. Returns the persisted
 * criterion with attribution. The backend runs this out-of-band of the
 * extraction pipeline — no pipeline events are re-fired.
 */
export async function addCriterionV2(
  tenderId: string,
  request: AddCriterionRequestV2,
): Promise<CriterionAPIV2> {
  const { data } = await apiClient.post<CriterionAPIV2>(
    `/tenders/${tenderId}/criteria`,
    request,
    { baseURL: V2_BASE },
  );
  return data;
}
